import {
  ReceiptVerificationStatus,
  resolveAttachmentVerification,
} from 'src/models/receiptVerification.models'
import { TransactionDetailResponse } from 'src/models/transaction.models'

const isPendingReceiptVerificationStatus = (
  status: ReceiptVerificationStatus,
): boolean =>
  status === ReceiptVerificationStatus.Verifying ||
  status === ReceiptVerificationStatus.Missing

export const mergeTransactionDetailResponses = (
  old: TransactionDetailResponse,
  next: TransactionDetailResponse,
): TransactionDetailResponse => {
  // Several transaction mutations return list-shaped payloads. Keep fields
  // owned by transaction detail when those narrower responses omit them.
  const previousAttachments = old.receiptAttachments ?? []
  const nextAttachments = next.receiptAttachments ?? previousAttachments
  const previousAttachmentsById = new Map(
    previousAttachments.map((attachment) => [attachment.id, attachment]),
  )
  const preserveReceiptMatchingStatus =
    old.receiptMatchingStatus !== undefined &&
    !isPendingReceiptVerificationStatus(old.receiptMatchingStatus) &&
    (next.receiptMatchingStatus === undefined ||
      isPendingReceiptVerificationStatus(next.receiptMatchingStatus))

  return {
    ...next,
    receiptRecoveryCase:
      next.receiptRecoveryCase === undefined
        ? old.receiptRecoveryCase
        : next.receiptRecoveryCase,
    receiptMatchingStatus: preserveReceiptMatchingStatus
      ? old.receiptMatchingStatus
      : next.receiptMatchingStatus,
    receiptAttachments: nextAttachments.map((attachment) => {
      const previousAttachment = previousAttachmentsById.get(attachment.id)
      if (!previousAttachment) return attachment

      const previousVerification =
        resolveAttachmentVerification(previousAttachment)
      const incomingVerification = resolveAttachmentVerification(attachment)
      const shouldPreservePrevious =
        previousVerification !== undefined &&
        (incomingVerification === undefined ||
          (!isPendingReceiptVerificationStatus(previousVerification.status) &&
            isPendingReceiptVerificationStatus(incomingVerification.status)))

      if (!shouldPreservePrevious) return attachment

      return {
        ...attachment,
        verification: previousAttachment.verification,
        matchingStatuses: previousAttachment.matchingStatuses,
      }
    }),
  }
}
